import React from 'react';
import Select, { components, MultiValueProps, OptionProps, StylesConfig } from 'react-select';
import styled from 'styled-components';
import { LayoutConst } from '../../../../../core';

const CustomMultiValue = (props: MultiValueProps<any>) => {
  return null;
};

const OptionContainer = styled.div<{ isFocused: boolean; isDisabled: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  cursor: ${({ isDisabled }) => isDisabled ? 'not-allowed' : 'pointer'};
  background-color: ${({ isFocused, isDisabled, theme }) =>
    isFocused && !isDisabled
      ? (theme.dropdown?.bgColorHover || theme.mainDropdownStyles?.hoverBG || 'rgba(0, 0, 0, 0.05)')
      : 'transparent'};
  opacity: ${({ isDisabled }) => isDisabled ? 0.5 : 1};
  transition: background-color 0.2s, opacity 0.2s;
  font-size: 15px;
  color: inherit;

  @media (max-width: ${LayoutConst.screen.md}px) {
    font-size: 13px;
  }
`;

const Checkmark = styled.span`
  margin-left: 8px;
`;

const CustomOption = (props: OptionProps<any, true>) => {
  const { isSelected, isFocused, isDisabled, label, innerRef, innerProps } = props;
  
  return (
    <OptionContainer
      ref={innerRef} 
      {...innerProps}
      isFocused={isFocused}
      isDisabled={isDisabled}
    >
      <span>{label}</span>
      {isSelected && <Checkmark>✓</Checkmark>}
    </OptionContainer>
  );
};

const CustomClearIndicator = () => null;

const SelectedTextContainer = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
  font-size: 15px;
  color: ${(p) => p.theme.tags.textColor};

  @media (max-width: ${LayoutConst.screen.md}px) {
    font-size: 13px;
  }
`;

const MAX_DISPLAY_LENGTH = 40;
const TRUNCATE_LENGTH = 29;

const truncateText = (text: string): string => {
  return text.length > MAX_DISPLAY_LENGTH 
    ? text.substring(0, TRUNCATE_LENGTH) + '...' 
    : text;
};

const CustomValueContainer = (props: any) => {
  const { selectProps, children } = props;
  const selectedValues = selectProps.value || [];
  
  if (selectedValues.length > 0) {
    const labels = selectedValues.map((v: any) => v.label).join(', ');
    const displayText = truncateText(labels);
    
    return (
      <components.ValueContainer {...props}>
        <SelectedTextContainer>
          {displayText}
        </SelectedTextContainer>
        {children[1]}
      </components.ValueContainer>
    );
  }
  
  return <components.ValueContainer {...props}>{children}</components.ValueContainer>;
};

const getCustomSelectStyles = (theme: any, isMobile: boolean, is1440: boolean): StylesConfig<any, true> => {
  const selectBg = theme.approvalBlockStyles?.selectBg ?? theme.dropdown.bgColor;
  const selectHover = theme.dropdown.bgColorHover || theme.dropdown.hoverBgColor || '#f5f5f5';
  const isDarkTheme = theme.themeName === 'blackTheme' || theme.themeName === 'greyTheme' || theme.themeName === 'jhonataTheme';

  return {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: selectBg,
    borderColor: 'transparent',
    borderRadius: '3px',
    minHeight: is1440 ? '40px' : '48px',
    boxShadow: 'none',
    '&:hover': {
      borderColor: 'transparent',
    },
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: selectBg,
    borderRadius: '8px',
    zIndex: 100,
  }),
  menuList: (provided) => ({
    ...provided,
    '::-webkit-scrollbar': {
      width: '3px',
    },
    '::-webkit-scrollbar-track': {
      borderRadius: '10px',
      backgroundColor: 'transparent',
    },
    '::-webkit-scrollbar-thumb': {
      backgroundColor: '#6B7280',
      borderRadius: '10px',
    },
    '::-webkit-scrollbar-thumb:hover': {
      backgroundColor: '#9CA3AF',
    },
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? selectHover : selectBg,
    color: isDarkTheme ? '#FFFFFF' : theme.dropdown.textColor,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: selectHover,
    },
  }),
  multiValue: (provided) => ({
    ...provided,
    display: 'none',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: isDarkTheme ? '#FFFFFF' : theme.dropdown.textColor,
    opacity: 0.7,
    fontSize: isMobile ? '13px' : '15px',
  }),
  singleValue: (provided) => ({
    ...provided,
    color: isDarkTheme ? '#FFFFFF' : theme.dropdown.textColor,
  }),
  input: (provided) => ({
    ...provided,
    display: 'none',
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '2px 8px',
    overflow: 'hidden',
    flexWrap: 'nowrap',
    color: isDarkTheme ? '#FFFFFF' : theme.dropdown.textColor,
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  };
};

export interface BlockingReasonsSelectProps {
  options: { value: number; label: string }[];
  value: { value: number; label: string }[];
  placeholder: string;
  isDisabled?: boolean;
  onChange: (selected: any) => void;
  isOptionDisabled?: (option: any) => boolean;
  theme: any;
  isMobile: boolean;
  is1440: boolean;
}

export const BlockingReasonsSelect: React.FC<BlockingReasonsSelectProps> = ({
  options,
  value,
  placeholder,
  isDisabled,
  onChange,
  isOptionDisabled,
  theme,
  isMobile,
  is1440,
}) => {
  return (
    <Select
      options={options}
      value={value}
      placeholder={placeholder}
      isMulti={true}
      isOptionDisabled={isOptionDisabled}
      onChange={onChange}
      isDisabled={isDisabled}
      styles={getCustomSelectStyles(theme, isMobile, is1440)}
      components={{
        MultiValue: CustomMultiValue,
        Option: CustomOption,
        ValueContainer: CustomValueContainer,
        ClearIndicator: CustomClearIndicator,
      }}
      hideSelectedOptions={false}
      closeMenuOnSelect={true}
      isSearchable={false}
      isClearable={false}
    />
  );
};
import React from 'react';
import Select, { components, MultiValueProps, OptionProps, StylesConfig } from 'react-select';
import styled from 'styled-components';
import { LayoutConst } from '../../../../../core';

const CustomMultiValue = (props: MultiValueProps<any>) => {
  return null;
};

const OptionContainer = styled.div<{ isFocused: boolean; isDisabled: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  cursor: ${({ isDisabled }) => isDisabled ? 'not-allowed' : 'pointer'};
  background-color: ${({ isFocused, isDisabled, theme }) =>
    isFocused && !isDisabled
      ? (theme.dropdown?.bgColorHover || theme.mainDropdownStyles?.hoverBG || 'rgba(0, 0, 0, 0.05)')
      : 'transparent'};
  opacity: ${({ isDisabled }) => isDisabled ? 0.5 : 1};
  transition: background-color 0.2s, opacity 0.2s;
  font-size: 15px;
  color: inherit;

  @media (max-width: ${LayoutConst.screen.md}px) {
    font-size: 13px;
  }
`;

const Checkmark = styled.span`
  margin-left: 8px;
`;

const CustomOption = (props: OptionProps<any, true>) => {
  const { isSelected, isFocused, isDisabled, label, innerRef, innerProps } = props;
  
  return (
    <OptionContainer
      ref={innerRef} 
      {...innerProps}
      isFocused={isFocused}
      isDisabled={isDisabled}
    >
      <span>{label}</span>
      {isSelected && <Checkmark>✓</Checkmark>}
    </OptionContainer>
  );
};

const CustomClearIndicator = () => null;

const SelectedTextContainer = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
  font-size: 15px;
  color: ${(p) => p.theme.tags.textColor};

  @media (max-width: ${LayoutConst.screen.md}px) {
    font-size: 13px;
  }
`;

const MAX_DISPLAY_LENGTH = 40;
const TRUNCATE_LENGTH = 29;

const truncateText = (text: string): string => {
  return text.length > MAX_DISPLAY_LENGTH 
    ? text.substring(0, TRUNCATE_LENGTH) + '...' 
    : text;
};

const CustomValueContainer = (props: any) => {
  const { selectProps, children } = props;
  const selectedValues = selectProps.value || [];
  
  if (selectedValues.length > 0) {
    const labels = selectedValues.map((v: any) => v.label).join(', ');
    const displayText = truncateText(labels);
    
    return (
      <components.ValueContainer {...props}>
        <SelectedTextContainer>
          {displayText}
        </SelectedTextContainer>
        {children[1]}
      </components.ValueContainer>
    );
  }
  
  return <components.ValueContainer {...props}>{children}</components.ValueContainer>;
};

const getCustomSelectStyles = (theme: any, isMobile: boolean, is1440: boolean): StylesConfig<any, true> => {
  const selectBg = theme.approvalBlockStyles?.selectBg ?? theme.dropdown.bgColor;
  const selectHover = theme.dropdown.bgColorHover || theme.dropdown.hoverBgColor || '#f5f5f5';
  const isDarkTheme = theme.themeName === 'blackTheme' || theme.themeName === 'greyTheme' || theme.themeName === 'jhonataTheme';

  return {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: selectBg,
    borderColor: 'transparent',
    borderRadius: '3px',
    minHeight: is1440 ? '40px' : '48px',
    boxShadow: 'none',
    '&:hover': {
      borderColor: 'transparent',
    },
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: selectBg,
    borderRadius: '8px',
    zIndex: 100,
  }),
  menuList: (provided) => ({
    ...provided,
    '::-webkit-scrollbar': {
      width: '3px',
    },
    '::-webkit-scrollbar-track': {
      borderRadius: '10px',
      backgroundColor: 'transparent',
    },
    '::-webkit-scrollbar-thumb': {
      backgroundColor: '#6B7280',
      borderRadius: '10px',
    },
    '::-webkit-scrollbar-thumb:hover': {
      backgroundColor: '#9CA3AF',
    },
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? selectHover : selectBg,
    color: isDarkTheme ? '#FFFFFF' : theme.dropdown.textColor,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: selectHover,
    },
  }),
  multiValue: (provided) => ({
    ...provided,
    display: 'none',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: isDarkTheme ? '#FFFFFF' : theme.dropdown.textColor,
    opacity: 0.7,
    fontSize: isMobile ? '13px' : '15px',
  }),
  singleValue: (provided) => ({
    ...provided,
    color: isDarkTheme ? '#FFFFFF' : theme.dropdown.textColor,
  }),
  input: (provided) => ({
    ...provided,
    display: 'none',
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '2px 8px',
    overflow: 'hidden',
    flexWrap: 'nowrap',
    color: isDarkTheme ? '#FFFFFF' : theme.dropdown.textColor,
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  };
};

export interface BlockingReasonsSelectProps {
  options: { value: number; label: string }[];
  value: { value: number; label: string }[];
  placeholder: string;
  isDisabled?: boolean;
  onChange: (selected: any) => void;
  isOptionDisabled?: (option: any) => boolean;
  theme: any;
  isMobile: boolean;
  is1440: boolean;
}

export const BlockingReasonsSelect: React.FC<BlockingReasonsSelectProps> = ({
  options,
  value,
  placeholder,
  isDisabled,
  onChange,
  isOptionDisabled,
  theme,
  isMobile,
  is1440,
}) => {
  return (
    <Select
      options={options}
      value={value}
      placeholder={placeholder}
      isMulti={true}
      isOptionDisabled={isOptionDisabled}
      onChange={onChange}
      isDisabled={isDisabled}
      styles={getCustomSelectStyles(theme, isMobile, is1440)}
      components={{
        MultiValue: CustomMultiValue,
        Option: CustomOption,
        ValueContainer: CustomValueContainer,
        ClearIndicator: CustomClearIndicator,
      }}
      hideSelectedOptions={false}
      closeMenuOnSelect={true}
      isSearchable={false}
      isClearable={false}
    />
  );
};
